// src/components/features/timetables/TimetableMap/TimetableMap.tsx
import type { ComponentProps } from 'preact';
import { useMemo } from 'preact/hooks';
import { Map } from '../../../base/Map/Map';
import { StateMessage } from '../../../base/StateMessage/StateMessage';
import { StateWrapper } from '../../../base/StateWrapper/StateWrapper';
import {
  useTimetableMap,
  type UseTimetableMapState,
} from '../../../../data/useTimetableMap';
import type { TimetableDetailsLine } from '../../../../data/timetableDetailsLine';
import { LoadingSpinner } from '../../../base/LoadingSpinner/LoadingSpinner';

export interface TimetableMapProps extends ComponentProps<'section'> {
  selectedLine: TimetableDetailsLine | null;
}

export interface TimetableMapContentProps extends ComponentProps<'section'> {
  selectedLine: TimetableDetailsLine | null;
  mapState: UseTimetableMapState;
}

export function TimetableMapContent({
  selectedLine,
  mapState,
  ...props
}: TimetableMapContentProps) {
  const {
    loading,
    error,
    unavailableMessage,
    lines,
    points,
    markers,
    fitCoordinates,
  } = mapState;

  if (!selectedLine) {
    return (
      <StateWrapper>
        <StateMessage
          state="error"
          title="Timetable map unavailable"
          message="Choose a timetable service to view its map."
        />
      </StateWrapper>
    );
  }

  const hasMapData =
    lines.length > 0 || points.length > 0 || markers.length > 0;

  const fallbackMessage = useMemo(() => {
    if (unavailableMessage) {
      return unavailableMessage;
    }

    return 'No map data is available for this service yet.';
  }, [unavailableMessage]);

  return (
    <StateWrapper
      loading={loading}
      error={error}
      isEmpty={!loading && !error && !hasMapData}
      renderLoading={() => <LoadingSpinner />}
      renderError={(lookupError) => (
        <StateMessage
          state="error"
          title="Map lookup failed"
          message={lookupError.message}
        />
      )}
      renderEmpty={() => (
        <StateMessage
          state="empty"
          title="Map unavailable"
          message={fallbackMessage}
        />
      )}
    >
      <Map
        aria-label="Timetable route map"
        lines={lines}
        points={points}
        markers={markers}
        fitCoordinates={fitCoordinates}
        {...props}
      />
    </StateWrapper>
  );
}

export function TimetableMap({ selectedLine, ...props }: TimetableMapProps) {
  const mapState = useTimetableMap(selectedLine);

  return (
    <TimetableMapContent
      selectedLine={selectedLine}
      mapState={mapState}
      {...props}
    />
  );
}
